/**
 * 时间的存与显 —— Y-03 的落地处:**统一存 UTC,展示按 Asia/Shanghai 转换**。
 *
 * ═══ 为什么这件事必须有一个单点 ═══
 *
 * 库里的时刻一律是 UTC(带时区的 datetime,序列化出去是 ISO 8601 的 Z 结尾)。
 * 而「这是几号几点的单」这句话,对客户、飞手、运营、海关四个身份说的是**东八区**。
 * 两端之间隔着一次转换,而这次转换错了不报错:
 *
 *   · 容器 TZ=UTC 时用本机 getter 直接显示 → 每天 08:00 之前的单显示成前一天,
 *     对账时表现为「怎么少了一天的单」(Y-10 的单号日期就是被这件事咬过一口)
 *   · 把日期字符串 `2026-09-13` 当成 UTC 零点过滤 → 大屏的「今天」实际是
 *     北京时间 08:00 到今天 08:00,而读的人以为它是一整个自然日
 *
 * 两种失效都不会抛、不会告警、界面上看起来完全正常。所以转换只写在这里一份,
 * 每个端引用它 —— 抄一份的下场是两处只改了一处,而漂移之后两边都显示正常。
 *
 * ═══ 东八区的偏移为什么写字面量而不是 Intl ═══
 *
 * 理由与 order-no.ts 那段相同(中国大陆自 1991 年起不再实行夏令时,
 * Asia/Shanghai 恒为 UTC+8):Intl 依赖运行时的 ICU 数据,
 * small-icu 的 node 镜像上会直接抛 —— 而那时最自然的「修法」就是退回本机时区,
 * 恰恰是要消灭的那件事。
 *
 * **偏移这个数字全仓只有一处字面量**(order-no.ts 的 480,guards/check-window-status-tz.mjs
 * 静态钉着它)。本文件引用它而不是复制第二个 480:两个 480 里改了一个,
 * 另一个会静默留在原地,而它管着的恰恰是跨日那一小时。
 */
import { ORDER_NO_TIMEZONE, ORDER_NO_UTC_OFFSET_MINUTES } from './order-no'

/** 展示时区。与单号那条业务规则(Y-10)是同一个时区,所以引用同一处 */
export const SHANGHAI_TIME_ZONE = ORDER_NO_TIMEZONE

/** 东八区相对 UTC 的偏移,分钟。唯一字面量在 order-no.ts(见文件头) */
export const SHANGHAI_UTC_OFFSET_MINUTES = ORDER_NO_UTC_OFFSET_MINUTES

/** 东八区的一刻,拆好的年月日时分秒 */
export interface ShanghaiParts {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

export class ShanghaiTimeError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ShanghaiTimeError'
  }
}

function toInstant(at: Date | string | number): Date {
  const d = at instanceof Date ? at : new Date(at)
  if (Number.isNaN(d.getTime())) {
    throw new ShanghaiTimeError(`拿到一个不合法的时间:${String(at)}`)
  }
  return d
}

/**
 * UTC 时刻 → 东八区的年月日时分秒。
 *
 * 实现是把时刻整体平移到东八区,再用 `getUTC*` 读 —— **一个本机时区的 getter 都不出现**。
 * 本机时区来自部署环境(容器默认 UTC,开发机可能是别的),而「这是几号的单」
 * 不该随部署环境变化。
 */
export function toShanghaiParts(at: Date | string | number): ShanghaiParts {
  const shifted = new Date(toInstant(at).getTime() + SHANGHAI_UTC_OFFSET_MINUTES * 60_000)
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: shifted.getUTCHours(),
    minute: shifted.getUTCMinutes(),
    second: shifted.getUTCSeconds(),
  }
}

/** 展示用日期 `YYYY-MM-DD`(东八区) */
export function formatShanghaiDate(at: Date | string | number): string {
  const p = toShanghaiParts(at)
  return `${pad(p.year, 4)}-${pad(p.month, 2)}-${pad(p.day, 2)}`
}

/** 展示用时刻 `YYYY-MM-DD HH:mm:ss`(东八区)。界面上出现时刻的地方一律用它 */
export function formatShanghaiDateTime(at: Date | string | number): string {
  const p = toShanghaiParts(at)
  return `${pad(p.year, 4)}-${pad(p.month, 2)}-${pad(p.day, 2)} ${pad(p.hour, 2)}:${pad(p.minute, 2)}:${pad(p.second, 2)}`
}

/** 展示用日期时间 `YYYY-MM-DD HH:mm`。大屏那种只到分钟的场合用 */
export function formatShanghaiMinute(at: Date | string | number): string {
  const p = toShanghaiParts(at)
  return `${pad(p.year, 4)}-${pad(p.month, 2)}-${pad(p.day, 2)} ${pad(p.hour, 2)}:${pad(p.minute, 2)}`
}

const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * `YYYY-MM-DD`(东八区的一个自然日)→ 那一天的 00:00 对应的 UTC 时刻。
 *
 * **认不出来就返回 null,不做任何猜测** —— 猜出来的日期会用在「今天有多少单」这种
 * 要拿去给人看的数字上。调用方拿到 null 必须显式拒绝(400),
 * 不许退回「不筛选」:那会把一句写错的日期静默翻译成「全部数据」。
 */
export function parseShanghaiDayStart(day: unknown): Date | null {
  if (typeof day !== 'string') return null
  const m = DAY_PATTERN.exec(day)
  if (!m) return null
  const year = Number(m[1])
  const month = Number(m[2])
  const date = Number(m[3])
  // 用 Date.UTC 构造,再减掉偏移 —— 构造出来的如果是非法日期(比如 2026-02-30),
  // Date.UTC 会把它滚到下个月,所以必须回读一遍确认没被滚走
  const utcMidnight = Date.UTC(year, month - 1, date)
  const probe = new Date(utcMidnight)
  if (probe.getUTCFullYear() !== year || probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== date) {
    return null
  }
  return new Date(utcMidnight - SHANGHAI_UTC_OFFSET_MINUTES * 60_000)
}

/**
 * 时间区间的开闭:`[from 的东八区零点, to 的次日东八区零点)`。
 *
 * 两个参数都可以不传(不传那一端就是不设限)。传了就必须是合法的 `YYYY-MM-DD`,
 * 否则抛 —— **不许把认不出来的日期当成「不筛选」**:大屏上「今天」与「全部」长得一样,
 * 而读的人以为自己在看今天。
 */
export function shanghaiDayRange(
  from?: unknown,
  to?: unknown,
): { fromUtc: Date | null; toUtcExclusive: Date | null } {
  const fromUtc = from === undefined || from === null || from === '' ? null : parseShanghaiDayStart(from)
  const toUtcExclusive = to === undefined || to === null || to === '' ? null : parseShanghaiDayStart(to)
  if (from !== undefined && from !== null && from !== '' && fromUtc === null) {
    throw new ShanghaiTimeError(`from 必须是 YYYY-MM-DD(东八区自然日),拿到 ${JSON.stringify(from)}`)
  }
  if (to !== undefined && to !== null && to !== '' && toUtcExclusive === null) {
    throw new ShanghaiTimeError(`to 必须是 YYYY-MM-DD(东八区自然日),拿到 ${JSON.stringify(to)}`)
  }
  if (fromUtc !== null && toUtcExclusive !== null && fromUtc.getTime() > toUtcExclusive.getTime()) {
    // 反向区间会让「全部数据」变成空集合,而大屏上只是几个 0 —— 那读起来像「今天没单」
    throw new ShanghaiTimeError(
      `from(${String(from)})比 to(${String(to)})还晚 —— 反向区间会把数据静默筛成空,` +
        '而大屏上只是几个 0,读起来像「这段时间没有单」',
    )
  }
  // to 含当天:闭区间的右端在实现上是「次日零点」(左闭右开)
  return {
    fromUtc,
    toUtcExclusive: toUtcExclusive === null ? null : new Date(toUtcExclusive.getTime() + 24 * 3600_000),
  }
}

function pad(n: number, width: number): string {
  return String(n).padStart(width, '0')
}
